import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Container, Row, Col } from 'reactstrap'
import { AllBoardsQuery } from './Queries'
import { HasPermissionsQuery } from '../Functions/querys'
import BoardActionsMenu from './TaskComponents/BoardActionsMenu'
import TaskManagementViewContainer from './TaskManagementViewContainer'
import { getDefaultTaskColumnConfig } from './taskTableConfig'
import './TaskManagementContainer.css'


// The sidebar query no longer carries each board's columns -- only the active board's are
// ever used, and nesting them cost a query per board. TaskManagementViewContainer fetches
// the active board's columns and appends them to this base config.
//
// My-Tasks-ness is derived from the route (`boardId`), never from whether the board happens
// to already be in `boards`. AllBoardsQuery pages in 30 at a time and resolves independently
// of the task list, so on a hard refresh -- or for a user with more than 30 boards -- a real
// board id can be looked up against an empty or incomplete `boards` list. Deriving from
// `boards.find(...)` would then read as "no active board", handing the board view the My
// Tasks aggregate column (and its server-side FK prefetch) plus the My Tasks preference key,
// sometimes permanently. `TaskManagementViewContainer` derives `isBoardView` from `boardId`
// the same way, and the two must agree or the config here silently mismatches which query
// variables and preferences it actually renders with.
const getTaskBoardColumnConfig = (boards = [], boardId) => {
    const isMyTasks = !boardId || boardId === "tasks"
    const activeBoard = isMyTasks ? null : boards.find(({ node }) => node.id === boardId)?.node || null

    return {
        activeBoard,
        // My Tasks spans every board, so it cannot show a board's FK columns
        // individually -- the aggregate column stands in for them there only.
        defaultColumnConfig: getDefaultTaskColumnConfig({ includeRelatedObjects: isMyTasks }),
        tablePreferenceId: isMyTasks ? "my_tasks_board_preferences" : `task_board_${boardId}_preferences`,
    }
}


// Small overlay on the board's tile marking who can see it. `isPrivate` is only rendered
// when it actually came back as a boolean -- a board whose visibility we don't know is
// better left unlabelled than shown as public, since the model defaults to private.
function BoardVisibilityBadge({ isPrivate }) {
    if (typeof isPrivate !== "boolean") {
        return null
    }
    const label = isPrivate ? "Private board" : "Public board"

    return (
        <span className={`board-visibility-badge ${isPrivate ? "is-private" : "is-public"}`} title={label}>
            <i className={isPrivate ? "fa fa-lock" : "fa fa-globe"} aria-hidden="true" />
            <span className="sr-only">{label}</span>
        </span>
    )
}


// The sidebar list and the main column run out of boards at the same moment, so both render
// this. Neither is a dead end: the only other way back to the active boards is the footer
// toggle, which is easy to miss when the panel it sits under is empty.
function ArchivedBoardsEmptyState({ isCompact = false, onBackToActiveBoards }) {
    return (
        <div className={`task-board-empty${isCompact ? " is-compact" : ""}`}>
            <span className="task-board-empty-icon">
                <i className="fa fa-archive" aria-hidden="true" />
            </span>
            <p className="task-board-empty-title">No archived boards</p>
            <p className="task-board-empty-body">
                {isCompact
                    ? "Boards you archive will be kept here."
                    : "Nothing has been archived yet. When you archive a board it moves here, and you can restore it any time."}
            </p>
            <button type="button" className="task-board-empty-action" onClick={onBackToActiveBoards}>
                <i className="fa fa-chevron-left" aria-hidden="true" />
                Back to active boards
            </button>
        </div>
    )
}

// Placeholder rows shown while the first page of boards is in flight. Matching the real
// item's shape keeps the list from jumping when the data lands.
const BOARD_SKELETON_ROWS = [0, 1, 2, 3]

// Exported unwrapped for tests -- the default export is bound to the boards query, which
// makes the loading states awkward to drive.
export function TaskManagementContainer({ currentUser }) {
    const { boardId } = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const [showArchivedBoards, setShowArchivedBoards] = useState(location.state?.taskBoardMode === "archived")
    const selectFirstArchivedBoardRef = useRef(false)
    const { data, loading, fetchMore } = useQuery(AllBoardsQuery, {
        variables: {
            orderBy: ["date_added"],
            isArchived: showArchivedBoards,
            first: 30,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "cache-and-network",
    })
    // Board permissions are per user, not per board, so they are asked for once here rather
    // than inside BoardActionsMenu -- that component is rendered per row, and asking there
    // put one request per board on the wire.
    const { data: boardPermissionData } = useQuery(HasPermissionsQuery, {
        variables: { userPermissions: ["change_board", "delete_board"] },
    })
    // hasPermissions only answers for the codenames asked for above; adding a check here
    // means adding its codename to that list too, or the check is silently always false.
    const boardPermissions = boardPermissionData?.hasPermissions || []
    const canChangeBoard = boardPermissions.includes("change_board")
    const canDeleteBoard = boardPermissions.includes("delete_board")
    const allBoards = data?.allBoards
    const boards = allBoards?.edges || []
    const showMyTasks = !showArchivedBoards
    const myTasksCount = showMyTasks ? 1 : 0
    const loadedBoardCount = boards.length + myTasksCount
    const totalBoardCount = (allBoards?.pageInfo?.length || boards.length) + myTasksCount
    // The query sets notifyOnNetworkStatusChange, so `loading` also covers fetchMore.
    // Having boards already is what separates the first load from paging.
    const isInitialBoardLoad = loading && boards.length === 0
    const isLoadingMoreBoards = loading && boards.length > 0
    const { activeBoard, defaultColumnConfig, tablePreferenceId } = useMemo(
        () => getTaskBoardColumnConfig(boards, boardId),
        [boards, boardId])
    const activeBoardTitle = !boardId || boardId === "tasks" ? "My Tasks" : activeBoard?.title || "Board"

    const handleBoardArchiveStatusChange = ({ boardId: changedBoardId, isArchived }) => {
        if (changedBoardId !== boardId) return
        if (isArchived) {
            navigate("/boards/tasks/", { replace: true })
            return
        }
        const nextArchivedBoard = boards.find(({ node }) => node.id !== changedBoardId)?.node
        if (nextArchivedBoard) {
            navigate(`/boards/${nextArchivedBoard.id}/tasks/`, {
                replace: true,
                state: { taskBoardMode: "archived" },
            })
            return
        }
    }

    useEffect(() => {
        if (!showArchivedBoards || loading || !selectFirstArchivedBoardRef.current) return

        selectFirstArchivedBoardRef.current = false
        const firstArchivedBoard = boards[0]?.node
        if (firstArchivedBoard) {
            navigate(`/boards/${firstArchivedBoard.id}/tasks/`, {
                replace: true,
                state: { taskBoardMode: "archived" },
            })
        }
    }, [boards, loading, navigate, showArchivedBoards])

    const handleBoardScopeChange = () => {
        const nextArchivedState = !showArchivedBoards
        selectFirstArchivedBoardRef.current = nextArchivedState
        setShowArchivedBoards(nextArchivedState)
        if (!nextArchivedState) {
            navigate("/boards/tasks/", { replace: true })
        }
    }

    const loadMoreEntries = () => {
        if (!allBoards?.pageInfo?.hasNextPage) return Promise.resolve()

        return fetchMore({
            variables: {
                cursor: allBoards.pageInfo.endCursor,
                orderBy: ["date_added"],
                isArchived: showArchivedBoards,
                first: 30,
            },
            updateQuery: (previousResult, { fetchMoreResult }) => ({
                allBoards: {
                    edges: [
                        ...previousResult.allBoards.edges,
                        ...fetchMoreResult.allBoards.edges,
                    ],
                    pageInfo: fetchMoreResult.allBoards.pageInfo,
                    __typename: previousResult.allBoards.__typename,
                },
            }),
        })
    }

    return (
        <div className="TaskManagementContainer">
            <Container fluid>
                <Row>
                    <Col xs="2" className="TaskBoardColumn">
                        <aside className="task-board-sidebar">
                            <div className="task-board-sidebar-header">
                                <div>
                                    <div className="task-board-sidebar-eyebrow">Workspace</div>
                                    <h4 className="task-board-sidebar-title">Boards</h4>
                                </div>
                                <span className="task-board-sidebar-count">
                                    {isInitialBoardLoad
                                        ? <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                                        : loadedBoardCount}
                                </span>
                            </div>
                            <div className="task-board-sidebar-meta" aria-live="polite">
                                {isInitialBoardLoad
                                    ? "Loading boards..."
                                    : `Showing ${loadedBoardCount} of ${totalBoardCount} boards`}
                            </div>
                            <div className="task-board-sidebar-current">
                                <span className="task-board-sidebar-current-label">Viewing</span>
                                <span className="task-board-sidebar-current-value">{activeBoardTitle}</span>
                            </div>
                            <div className="task-board-list-scroll">
                                <ul className="TaskBoardList">
                                    {showMyTasks && (
                                        <li className={!boardId || boardId === 'tasks' ? 'active' : ''}>
                                            <div className="board-sidebar-item">
                                                <Link className="board-sidebar-link" to="/boards/tasks/">
                                                    <span className="board-sidebar-icon"><i className="fa fa-inbox" aria-hidden="true" /></span>
                                                    <span className="board-sidebar-content">
                                                        <span className="board-sidebar-title-text">My Tasks</span>
                                                        <span className="board-sidebar-subtitle">Personal task queue</span>
                                                    </span>
                                                </Link>
                                            </div>
                                        </li>
                                    )}
                                    {boards.map((boardEdge) => (
                                        <li className={boardId === boardEdge.node.id ? 'active' : ''} key={boardEdge.node.id}>
                                            <div className="board-sidebar-item">
                                                <Link
                                                    className="board-sidebar-link"
                                                    to={`/boards/${boardEdge.node.id}/tasks/`}
                                                    state={showArchivedBoards ? { taskBoardMode: "archived" } : undefined}
                                                >
                                                    <span className="board-sidebar-icon">
                                                        <i className="fa fa-columns" aria-hidden="true" />
                                                        <BoardVisibilityBadge isPrivate={boardEdge.node.isPrivate} />
                                                    </span>
                                                    <span className="board-sidebar-content">
                                                        <span className="board-sidebar-title-text">{boardEdge.node.title}</span>
                                                        <span className="board-sidebar-subtitle">
                                                            {boardEdge.node.description || `Board #${boardEdge.node.pk}`}
                                                        </span>
                                                    </span>
                                                </Link>
                                                <span className="board-edit-icon board-actions-wrapper" onClick={(e) => e.stopPropagation()}>
                                                    <BoardActionsMenu
                                                        board={boardEdge.node}
                                                        canChangeBoard={canChangeBoard}
                                                        canDeleteBoard={canDeleteBoard}
                                                        onArchiveStatusChange={handleBoardArchiveStatusChange}
                                                    />
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                    {isInitialBoardLoad && BOARD_SKELETON_ROWS.map((rowIndex) => (
                                        <li key={`board-skeleton-${rowIndex}`} aria-hidden="true">
                                            <div className="board-sidebar-item board-sidebar-item-skeleton">
                                                <span className="board-sidebar-link">
                                                    <span className="board-sidebar-icon board-skeleton-shape" />
                                                    <span className="board-sidebar-content">
                                                        <span className="board-skeleton-shape board-skeleton-line-title" />
                                                        <span className="board-skeleton-shape board-skeleton-line-subtitle" />
                                                    </span>
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                {showArchivedBoards && !loading && boards.length === 0 && (
                                    <ArchivedBoardsEmptyState
                                        isCompact
                                        onBackToActiveBoards={handleBoardScopeChange}
                                    />
                                )}
                                {allBoards?.pageInfo?.hasNextPage && (
                                    <button
                                        type="button"
                                        className="task-board-load-more"
                                        onClick={loadMoreEntries}
                                        disabled={isLoadingMoreBoards}
                                    >
                                        <span>{isLoadingMoreBoards ? "Loading boards..." : "Load more boards"}</span>
                                        <i
                                            className={isLoadingMoreBoards ? "fa fa-spinner fa-spin" : "fa fa-arrow-down"}
                                            aria-hidden="true"
                                        />
                                    </button>
                                )}
                            </div>
                            <button
                                type="button"
                                className={`task-board-archive-toggle${showArchivedBoards ? " is-active-boards" : ""}`}
                                onClick={handleBoardScopeChange}
                                aria-pressed={showArchivedBoards}
                            >
                                <span className="task-board-archive-toggle-icon">
                                    <i className={showArchivedBoards ? "fa fa-columns" : "fa fa-archive"} aria-hidden="true" />
                                </span>
                                <span className="task-board-archive-toggle-label">
                                    {showArchivedBoards ? "Active boards" : "Archived boards"}
                                </span>
                                <span className="task-board-archive-toggle-meta">
                                    <i className={showArchivedBoards ? "fa fa-chevron-left" : "fa fa-chevron-right"} aria-hidden="true" />
                                </span>
                            </button>
                        </aside>
                    </Col>
                    <Col xs="10" className="TaskListColumn">
                        {showArchivedBoards && !loading && boards.length === 0 ? (
                            <div className="task-board-empty-view">
                                <ArchivedBoardsEmptyState onBackToActiveBoards={handleBoardScopeChange} />
                            </div>
                        ) : (
                            <TaskManagementViewContainer
                                currentUser={currentUser}
                                defaultColumnConfig={defaultColumnConfig}
                                tablePreferenceId={tablePreferenceId}
                            />
                        )}
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

export default TaskManagementContainer
